/**
 * SEO HEADING STRUCTURE - OnlineStudyPlatformTemplate
 * ====================================================
 * 
 * TITLE: {platformName} Review | CSW | Online Study Platform
 * META DESCRIPTION: Unique per page (passed via seoDescription prop)
 * 
 * H1 (1 tag):
 *   - "{platformName}" OR "{platformName} Alternative" (if useAlternativeH1=true)
 * 
 * H2 (6 tags):
 *   - "Key Features"
 *   - "Pros & Cons"
 *   - "User Feedback"
 *   - "Pricing"
 *   - "Best Use Cases"
 *   - "Community & Peer Learning"
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { 
  ArrowRight, 
  CheckCircle2,
  XCircle,
  ExternalLink,
  DollarSign,
  Users,
  Target,
  MessageSquare,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";
import { LucideIcon } from "lucide-react";

interface HreflangTag {
  lang: string;
  href: string;
}

interface OnlineStudyPlatformTemplateProps {
  platformName: string;
  platformUrl: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalUrl: string;
  heroDescription: string;
  category: string;
  icon: LucideIcon;
  features: string[];
  pros: string[];
  cons: string[];
  userFeedback: string;
  pricing: string;
  bestUseCases: string;
  communityLearning: string;
  hreflangTags?: HreflangTag[];
  useAlternativeH1?: boolean;
  parentPage?: { name: string; path: string };
}

export const OnlineStudyPlatformTemplate = ({
  platformName,
  platformUrl,
  seoTitle,
  seoDescription,
  seoKeywords,
  canonicalUrl,
  heroDescription,
  category,
  icon: PlatformIcon,
  features,
  pros,
  cons,
  userFeedback,
  pricing,
  bestUseCases,
  communityLearning,
  hreflangTags,
  useAlternativeH1 = false,
  parentPage = { name: "Online Study", path: "/online-study/" },
}: OnlineStudyPlatformTemplateProps) => {
  // Normalize canonical URL to always be a full URL for JSON-LD
  const fullCanonicalUrl = canonicalUrl.startsWith("https://") || canonicalUrl.startsWith("http://")
    ? canonicalUrl
    : `https://csw.live${canonicalUrl}`;
  
  // Normalize to path only for SEO component
  const canonicalPath = canonicalUrl.startsWith("https://csw.live")
    ? canonicalUrl.replace("https://csw.live", "")
    : canonicalUrl;

  // Generate JSON-LD structured data
  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": fullCanonicalUrl,
    "keywords": seoKeywords,
    "inLanguage": "en",
    "dateModified": new Date().toISOString().split('T')[0],
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://csw.live"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": parentPage.name,
          "item": `https://csw.live${parentPage.path}`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": useAlternativeH1 ? `${platformName} Alternative` : platformName,
          "item": fullCanonicalUrl
        }
      ]
    },
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": platformName,
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web",
      "url": platformUrl,
      "description": heroDescription
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW",
      "url": "https://csw.live"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={canonicalPath}
        hreflangTags={hreflangTags}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Breadcrumb */}
            <nav className="flex justify-center items-center gap-2 text-sm text-muted-foreground animate-fade-in">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span>/</span>
              <Link to={parentPage.path} className="hover:text-primary transition-colors">{parentPage.name}</Link>
              <span>/</span>
              <span className="text-foreground">{useAlternativeH1 ? `${platformName} Alternative` : platformName}</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <PlatformIcon className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">{category.toUpperCase()}</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="text-gradient">{platformName}{useAlternativeH1 ? " Alternative" : ""}</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {heroDescription}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                asChild
              >
                <a href={platformUrl} target="_blank" rel="noopener noreferrer">
                  Visit {platformName} <ExternalLink className="ml-2 w-4 h-4" />
                </a>
              </Button>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Key <span className="text-gradient">Features</span>
              </h2>
            </div>

            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <ul className="space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-muted-foreground">
                    <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Pros & Cons Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Pros & <span className="text-gradient">Cons</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                <h3 className="font-display text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                  Pros
                </h3>
                <ul className="space-y-4">
                  {pros.map((pro, i) => (
                    <li key={i} className="flex items-start gap-3 text-foreground">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      {pro}
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 bg-card/30 backdrop-blur-sm border-border/50">
                <h3 className="font-display text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <XCircle className="w-6 h-6 text-muted-foreground" />
                  Cons
                </h3>
                <ul className="space-y-4">
                  {cons.map((con, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      {con}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* User Feedback Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                User <span className="text-gradient">Feedback</span>
              </h2>
            </div>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="flex items-start gap-4">
                <MessageSquare className="w-8 h-8 text-primary flex-shrink-0" />
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {userFeedback}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                <span className="text-gradient">Pricing</span>
              </h2>
            </div>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="flex items-start gap-4">
                <DollarSign className="w-8 h-8 text-primary flex-shrink-0" />
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {pricing}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Best Use Cases Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Best <span className="text-gradient">Use Cases</span>
              </h2>
            </div>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="flex items-start gap-4">
                <Target className="w-8 h-8 text-primary flex-shrink-0" />
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {bestUseCases}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Community & Peer Learning Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-4">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Community & <span className="text-gradient">Peer Learning</span>
              </h2>
            </div>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="flex items-start gap-4">
                <Users className="w-8 h-8 text-primary flex-shrink-0" />
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {communityLearning}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-12 sm:p-16 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Ready to <span className="text-gradient">Start Studying?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of students using CSW for free virtual study rooms, productivity tools, and community learning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                asChild
              >
                <Link to="/online-study/">
                  Browse All Platforms
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnlineStudyPlatformTemplate;
